import type { Concert } from './domain/program-schema.ts'
import { defaultStorage, type StorageLike } from './store/annotations.ts'

/**
 * The offline artist images.
 *
 * The programme snapshot and the shell are already offline — bundled and precached —
 * so the one thing a concert page loses on a dead network is its picture. This module
 * owns that gap end to end: the URL an `<img>` asks for, the route the service worker
 * caches, and the warm-up that fills the cache while there is still signal (R24, R25).
 *
 * The three have to agree on one string each, which is why they live together rather
 * than beside their callers. A Cache Storage entry is keyed by URL, so a warm-up that
 * asks for anything other than exactly what the `<img>` asks for downloads five
 * megabytes that are never read.
 *
 * Imported by `src/sw.ts`, so nothing here may reach for the DOM, React or the
 * programme snapshot at module scope: it would all be dragged into the service worker
 * bundle. `localStorage` in particular does not exist in a worker at all, which is why
 * every access to it is behind a function the worker never calls.
 */

/**
 * Where the proxied images live. Also the Cache Storage name, passed to the service
 * worker's CacheFirst route verbatim — Workbox keeps a `cacheName` it is given.
 */
export const IMAGE_CACHE = 'oya-annotated:bilder:v1'

/** Marks the snapshot whose images are already downloaded. Read by the smoke test. */
export const IMAGE_WARMED_KEY = 'oya-annotated:bilder-hentet:v1'

const IMAGE_PATH = '/bilde/'

/** The same shape `server/images.js` accepts. Kept in step by the tests on both sides. */
/** Matches the proxy's own deadline in `server/images.js`. */
const WARM_UP_TIMEOUT_MS = 15_000

const ASSET_NAME = /^[a-f0-9]{40}-\d+x\d+\.(jpg|jpeg|png|webp)$/

export interface PreloadOptions {
  urls: readonly string[]
  /** The snapshot's `fetchedAt`. Warming the same one twice is skipped. */
  version?: string | null
  cacheName?: string
  fetchImpl?: typeof fetch | null
  cacheStorage?: CacheStorage | null
  storage?: StorageLike | null
  batchSize?: number
}

export interface PreloadResult {
  /** How many urls were missing and therefore requested. */
  requested: number
  stored: number
  failed: number
  /** True when the run was a no-op: already warmed, or nowhere to store anything. */
  skipped: boolean
}

/**
 * The same-origin url for a concert's artist image, or null when there is nothing we
 * can proxy.
 *
 * Validated client-side against the server's own allowlist rather than trusted: a
 * snapshot url the proxy would answer 400 is better dropped here than rendered as an
 * `<img>` that cannot load and a cache entry that can never be read.
 */
export function imageProxyUrl(image: string | null | undefined): string | null {
  if (typeof image !== 'string' || image === '') return null
  const asset = image.split('?')[0]?.split('/').pop() ?? ''
  return ASSET_NAME.test(asset) ? `${IMAGE_PATH}${asset}` : null
}

/** Every image the programme needs, in snapshot order, without duplicates. */
export function concertImageUrls(concerts: readonly Concert[]): string[] {
  const urls: string[] = []
  const seen = new Set<string>()
  for (const concert of concerts) {
    const url = imageProxyUrl(concert.image)
    if (!url || seen.has(url)) continue
    seen.add(url)
    urls.push(url)
  }
  return urls
}

/**
 * The service worker's image route, as a Workbox match callback.
 *
 * Exported so it is testable: `src/sw.ts` calls `self.skipWaiting()` at module scope
 * and cannot be imported by a test at all.
 */
export function matchImageRoute({ url, sameOrigin }: { url: URL; sameOrigin: boolean }): boolean {
  return sameOrigin && url.pathname.startsWith(IMAGE_PATH)
}

function defaultCaches(): CacheStorage | null {
  try {
    return typeof caches !== 'undefined' ? caches : null
  } catch {
    return null
  }
}

function defaultFetch(): typeof fetch | null {
  return typeof fetch === 'function' ? fetch.bind(globalThis) : null
}

function read(storage: StorageLike | null, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null
  } catch {
    return null
  }
}

function write(storage: StorageLike | null, key: string, value: string): void {
  try {
    storage?.setItem(key, value)
  } catch {
    // A full or blocked store means the warm-up runs again next load, which costs a
    // handful of cache hits and nothing else.
  }
}

/**
 * Downloads the images into the cache the service worker serves them from.
 *
 * Deliberately not `cache.addAll`, which rejects the whole set on a single failure —
 * one image gone from the CDN would leave the user with none of them (R25). Each url is
 * attempted on its own, in small batches so a festival network is not asked for 88
 * connections at once, and a run with any failure leaves the marker unwritten so the
 * next load retries the rest.
 */
export async function preloadImages({
  urls,
  version = null,
  cacheName = IMAGE_CACHE,
  fetchImpl = defaultFetch(),
  cacheStorage = defaultCaches(),
  storage = defaultStorage(),
  batchSize = 4,
}: PreloadOptions): Promise<PreloadResult> {
  const nothing = { requested: 0, stored: 0, failed: 0, skipped: true }

  if (version && read(storage, IMAGE_WARMED_KEY) === version) return nothing
  if (!cacheStorage || !fetchImpl) return nothing

  let cache: Cache
  try {
    cache = await cacheStorage.open(cacheName)
  } catch {
    return nothing
  }

  const missing: string[] = []
  for (const url of urls) {
    // One at a time: this is a cache lookup, not a download, and the alternative is 88
    // parallel opens competing with the first paint this whole module runs after.
    const hit = await cache.match(url).catch(() => undefined)
    if (!hit) missing.push(url)
  }

  let stored = 0
  let failed = 0

  for (let index = 0; index < missing.length; index += batchSize) {
    const batch = missing.slice(index, index + batchSize)
    await Promise.all(
      batch.map(async (url) => {
        try {
          /*
           * The same deadline the proxy gives itself. Without one a stalled connection
           * holds a slot in this batch forever, and on a festival network that is the
           * normal case rather than the exception — the warm-up would sit at three of
           * four in flight and never finish. An abort lands in the catch below, counts
           * as a failure, and so leaves the marker unwritten for the next load to retry.
           */
          const response = await fetchImpl(url, {
            credentials: 'omit',
            signal: AbortSignal.timeout(WARM_UP_TIMEOUT_MS),
          })
          // A 502 from the proxy is a CDN failure, and caching it would pin the
          // failure behind an immutable header. The service worker refuses it too.
          if (!response.ok) {
            failed += 1
            return
          }
          await cache.put(url, response)
          stored += 1
        } catch {
          failed += 1
        }
      }),
    )
  }

  if (version && failed === 0) write(storage, IMAGE_WARMED_KEY, version)

  return { requested: missing.length, stored, failed, skipped: false }
}
